import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './styles.scss';
import searchIcon from '../../assets/images/icons/search.svg';

const Search = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const navigate = useNavigate();
  const wrapperRef = useRef(null);

  const hasResults =
    results &&
    ((results.products?.length > 0) ||
      (results.slabs?.length > 0) ||
      (results.bases?.length > 0));

  // Debounced live search effect (only fires when query >= 3 chars)
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 3) {
      setResults(null);
      setShowDropdown(false);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_WEBSITE_API}/api/Website/search?query=${encodeURIComponent(trimmed)}`,
          { signal: controller.signal }
        );
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();
        setResults(data);
        setShowDropdown(true);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message);
          setResults(null);
          setShowDropdown(false);
        }
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderItem = (item, type) => {
    const path = type === 'products'
      ? item.productId ? `/products/${item.id}/${item.productId}` : `/products/${item.id}`
      : `/${type}/${item.id}`;

    return (
      <div
        key={`${type}-${item.id}`}
        className="search-dropdown-result"
        onClick={() => {
          navigate({ pathname: path, search: `?title=${item.title}` });
          setShowDropdown(false);
        }}
      >
        {item.images?.[0]?.fileName && (
          <img
            src={`${process.env.REACT_APP_WEBSITE_API}/api/images/optimize?filePath=images/${type}/${item.images[0].fileName}&width=50&height=50&quality=70&format=webp`}
            alt={item.title}
          />
        )}
        <span>{item.title}</span>
      </div>
    );
  };

  return (
    <div className="search-wrapper" ref={wrapperRef}>
      <div className="search-input-wrapper">
        <input
          type="text"
          placeholder="Search..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => hasResults && setShowDropdown(true)}
        />
        {loading ? (
          <div className="search-loader" />
        ) : (
          <button
            type="button"
            className="search-button"
            onClick={() => query.trim().length >= 3 && setQuery(query)}
          >
            <img src={searchIcon} alt="Search" />
          </button>
        )}
      </div>

      {error && (
        <div className="search-dropdown">
          <p style={{ color: 'red' }}>Error: {error}</p>
        </div>
      )}

      <div className={`search-dropdown ${showDropdown ? '' : 'hidden'}`}>
        {hasResults ? (
          <>
            {results.products?.length > 0 && (
              <div className="search-dropdown-section">
                <h3>Shop</h3>
                {results.products.map(item => renderItem(item, 'products'))}
              </div>
            )}
            {results.slabs?.length > 0 && (
              <div className="search-dropdown-section">
                <h3>Slabs</h3>
                {results.slabs.map(item => renderItem(item, 'slabs'))}
              </div>
            )}
            {results.bases?.length > 0 && (
              <div className="search-dropdown-section">
                <h3>Bases</h3>
                {results.bases.map(item => renderItem(item, 'bases'))}
              </div>
            )}
          </>
        ) : (
          <div className="search-dropdown-section">
            <h4>No results found</h4>
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;
